/**
 * @author: lv.chen
 * @date: 2025/7/3
 * @desc: Market Profile页面PCConsole榜单页
 */
import React, { useRef, useMemo, useState, useEffect } from 'react';
import classnames from 'classnames';
import { useIntl } from 'umi';
import { cloneDeep } from 'lodash-es';
import { useMemoizedFn } from 'ahooks';
import { Justify, PagingQuery } from 'tea-component';
import ExportTable from '@intelligence/components/ExportTable';
import GroupByGranularityMetricSelect from '@intelligence/v3/components/GroupByGranularityMetricSelect';
import GlobalPerformance from '@intelligence/v3/pages/MarketProfile/components/GlobalPerformance';
import MarketMetricsTable from '@intelligence/v3/pages/MarketProfile/components/MarketMetricsTable';
import Filter from '@intelligence/v3/pages/MarketProfile/PCAndConsoleGames/components/Filter';
import MetricSelectConfirmModal from '@intelligence/v3/pages/MarketProfile/PCAndConsoleGames/components/MetricSelectConfirmModal';
import SearchTable from '@intelligence/pages/MarketProfile/components/MarketTableCard/SearchTable';
import useRequest from '@gp/hooks/useRequest';
import useConfigureTexts from '@intelligence/hooks/useConfigureTexts';
import { useQueryDefinition } from '@intelligence/hooks/useQueryDefinition';
import useFilterUrlLinkage from '@intelligence/hooks/useFilterUrlLinkage';
import useDontShowAgain, { getIsShowByShowDate } from './_hooks/useDontShowAgain';
import { fetchGenreSourcesOptionsAll } from '@intelligence/components/GenreSelectMiddleware/services';
import { fetchGenreMarketProfileList, fetchGenreMarketProfileLineChartList } from '../services';
import { getDataSourceIcon } from '@intelligence/utils/getDataSourceImg';
import {
  getDateRangeFetchParamsByRangeTimeFilterValue,
  getRangeTimeFilterValueByDateRangeFetchParams,
} from '@intelligence/v3/utils/date';
import { getAllCountriesByRegions } from '@intelligence/v3/utils/region';
import { generateSortMetricKeys, multiRatioMetricSort } from '@intelligence/v3/utils/metric';
import { GENRE_SOURCE_REVERSE_MAP } from '@intelligence/v3/utils/genre';
import { addRank, calcMainGenres } from '@intelligence/v3/utils/topcharts';
import { getMetricName } from '@intelligence/v3/components/TableComponents/RatioMetricSortBy/utils';
import { reportButtonLog, reportSearchLog } from '@intelligence/v3/utils/logReporter';
import { getTopCountries, getMarketProfileMapChartData, getMarketProfileLineChartData } from '../utils';
import {
  DATASOURCE_EXPLANATION_KEY_LIST,
  DATASOURCE_EXPLANATION_DATASOURCE_KEY_LIST,
  YEARLY_METRIC_KEYS,
  getIsNewzooYearlyMetric,
  getIsMetricNoData,
  getGranularityMetrics,
  getGranularityMetric,
  getGranularitySorts,
  getMetricNameSpecial,
  formatFilterValue,
  appendFixedMetrics,
} from './utils';
// 导入HTML格式数据源解释配置
import {
  DATASOURCE_EXPLANATION_HTML_DATASOURCE_LIST,
  getDataSourceExplanationHtmlContent,
} from './utils/dataSourceExplanationHtmlConfig';
import { ChartType } from '@intelligence/v3/enums/chart';
import { DEFAULT_SIZE } from '@home/components/TeaComponents/Table/constants';
import { dbiPageLogMes, dbiBtnLogMes } from '@intelligence/utils/buriedPointCfg';
import * as filterKeyMap from '@intelligence/components/Filter/FilterBase/_config';
import { DATASOURCE_KEY_MAP } from '@intelligence/v3/_config/metrics/keyMap';
import { GSD_DIGITAL_REVENUE_METRIC_KEY } from '@intelligence/v3/_config/metrics/gsd';
import { DEFAULT_GSD_TYPE } from '@intelligence/v3/constants';
import { FILTER_URL_CONFIG } from '@intelligence/_config/components/filterUrl/combineMarketProfile';

import '../index.scss';

interface IMarketProfilePCConsoleGamesProps extends IBaseProps { }

/**
 * 是否为短链接模式
 */
const isShortMode = false;

/**
 * 是否需要解码url参数
 */
const needDecodeUrl = true;

/**
 * 是否要更新url
 */
const needUpdateUrl = true;

const defaultFilterValue = {
  [filterKeyMap.GENRETOR]: {
    source: 'iegg',
    mainGenres: [],
    subGenres: [],
  },
  [filterKeyMap.RANGE_TIME]: {
    type: 'month',
    value: [undefined, undefined],
  },
  [filterKeyMap.REGION]: {
    type: 'market',
    regions: [],
    markets: ['global'],
  },
  [filterKeyMap.PUBLISHER]: {
    value: DEFAULT_GSD_TYPE,
  },
};

const PCAndConsoleGames: React.FC<IMarketProfilePCConsoleGamesProps> = (props) => {
  const { style, className, entityType = '' } = props;

  const { locale } = useIntl();

  const { loading: genresOptionsResLoading, data: genresOptionsRes } = useRequest(fetchGenreSourcesOptionsAll, {
    defaultParams: [entityType],
  });

  /**
   * 配置为本
   */
  const { configureTexts } = useConfigureTexts();

  /**
   * 获取系统级配置数据
   * countryMap：国家映射，键为国家code、值为国家信息
   * regionOptions: 地区国家列表
   */
  const { countryMapNew: countryMap, metricTableRegionCountry: regionOptions } = useQueryDefinition();

  const countriesForSearch = useMemo(() => {
    const list = getAllCountriesByRegions(regionOptions);
    return list.map((item) => {
      const code = item?.country_code || '';
      const name = item?.country_name_en || '';
      return { code, enName: code, name };
    });
  }, [regionOptions]);

  const { showDate, updateDontShowAgain } = useDontShowAgain();

  /**
   * 筛选器value
   */
  const [filterValue, setFilterValue] = useState<IRecord>(() => cloneDeep(defaultFilterValue));

  const {
    isLoading = true,
    decodeObj,
    updateUrl,
  } = useFilterUrlLinkage({
    filterData: filterValue,
    // @ts-ignore
    config: FILTER_URL_CONFIG,
    isShortMode,
    needDecodeUrl,
    needUpdateUrl,
  });

  const updateFilterValue = (payload: IRecord) => {
    setFilterValue(payload);
    // @ts-ignore
    updateUrl?.(payload, FILTER_URL_CONFIG, isShortMode);
  };

  /**
   * 筛选器跳转二次确认弹窗visible
   */
  const [filterRedirectConfirmModalVisible, setFilterRedirectConfirmModalVisible] = useState(false);

  /**
   * 筛选器跳转二次确认的日期粒度
   */
  const [filterRedirectConfirmGranularity, setFilterRedirectConfirmGranularity] = useState<IGranularity>('');

  /**
   * 筛选器跳转二次确认的指标
   */
  const [filterRedirectConfirmMetric, setFilterRedirectConfirmMetric] = useState<IPrimaryKeyMetric | null>(null);

  /**
   * 是否为搜索模式
   */
  const [isSearchMode, setIsSearchMode] = useState(false);

  // 下载参数
  const [reqParams, setReqParams] = useState<any>({});

  /**
   * newzoo指标有数据的日期范围
   */
  const [newzooDateRange, setNewzooDateRange] = useState({ start_date: '', end_date: '' });

  /**
   * echarts图表类型
   * 1、世界地图
   * 2、折线图
   */
  const [chartType, setChartType] = useState<ChartType>(ChartType.Map);

  const granularityMetrics = getGranularityMetrics('monthlypcandconsole');

  /**
 * 全量指标，包含动态指标、固定展示的指标
 */
  const metrics = useMemo<ICommonMetric[]>(() => appendFixedMetrics(granularityMetrics), [granularityMetrics]);

  /**
   * 当前选中的指标（Global Performance模块的指标切换）
   */
  const [metric, setMetric] = useState<IPrimaryKeyMetric>(() => {
    const rangeTime = filterValue?.[filterKeyMap.RANGE_TIME];
    const dateRangeFetchParams = getDateRangeFetchParamsByRangeTimeFilterValue(rangeTime);
    return getGranularityMetric(dateRangeFetchParams.granularity);
  });

  /**
   * 当前勾选的国家
   * 注意：修改筛选器后，重新勾选列表的前6个国家（不包含Global、Global except China mainland）
   */
  const [countries, setCountries] = useState<string[]>([]);

  /**
   * 当前排序状态列表
   * 代码支持多字段排序，但实际业务目前只有单字段排序，预防后续业务调整支持多字段排序，所以这里直接用数组类型
   */
  const [sorts, setSorts] = useState<IRatioMetricSortState<IGenreMarketProfile>[]>(() => {
    const rangeTime = filterValue?.[filterKeyMap.RANGE_TIME];
    const dateRangeFetchParams = getDateRangeFetchParamsByRangeTimeFilterValue(rangeTime);
    return getGranularitySorts(dateRangeFetchParams.granularity);
  });

  /**
   * 分页
   */
  const [pagingQuery, setPagingQuery] = useState<PagingQuery>({ pageIndex: 1, pageSize: DEFAULT_SIZE });

  /**
   * 是否正在加载Market Profile列表数据
   */
  const [marketProfilesDataLoading, setMarketProfilesDataLoading] = useState(true);

  /**
   * Market Profile列表数据（按market），用于表格、echarts世界地图
   */
  const [marketProfilesData, setMarketProfilesData] = useState<IGenreMarketProfilePaginationData>({
    total: 0,
    list: [],
  });

  /**
   * Market Profile列表数据（按market），用于缓存搜索前的表格、echarts世界地图数据，清除搜索后恢复数据
   */
  const marketProfilesDataCached = useRef<IGenreMarketProfilePaginationData>({
    total: 0,
    list: [],
  });

  /**
   * 分页（缓存），用于清除搜索后，恢复分页状态
   */
  const pagingQueryCached = useRef<PagingQuery>({ pageIndex: 1, pageSize: DEFAULT_SIZE });

  /**
   * 是否正在加载Market Profile列表数据（按date、market拆分），仅用于echarts折线图
   */
  const [marketProfilesDataGroupedByDateLoading, setMarketProfilesDataGroupedByDateLoading] = useState(true);

  /**
   * Market Profile列表数据（按date、market拆分），仅用于echarts折线图
   */
  const [marketProfilesDataGroupedByDate, setMarketProfilesDataGroupedByDate] =
    useState<IGenreMarketProfilePaginationDataNewzoo>({ total: 0, list: [], newzoo_list: [] });

  /**
   * echarts世界地图数据
   */
  const mapData = useMemo<IMapChartData[]>(() => {
    if (marketProfilesDataLoading) return [];
    return getMarketProfileMapChartData(countryMap, marketProfilesData?.list, metric);
  }, [marketProfilesDataLoading, countryMap, marketProfilesData, metric]);

  /**
   * echarts折线图数据
   */
  const lineData = useMemo<ILineChartData[]>(() => {
    if (marketProfilesDataGroupedByDateLoading) return [];
    const list =
      metric?.dataSource === DATASOURCE_KEY_MAP.NEWZOO
        ? marketProfilesDataGroupedByDate?.newzoo_list
        : marketProfilesDataGroupedByDate?.list;
    return getMarketProfileLineChartData(list, metric, countries);
  }, [marketProfilesDataGroupedByDateLoading, marketProfilesDataGroupedByDate, metric, countries]);

  /**
   * 级联国家筛选器右侧的解释文案获取方法
   */
  const getRadioOptionsTips = useMemoizedFn(() => {
    return { ...configureTexts };
  });

  const getMetricNameSpecialInPage = useMemoizedFn((metric: IPrimaryKeyMetric, metricName: string) => {
    return getMetricNameSpecial(locale, {
      metric,
      metricName,
      startDate: newzooDateRange?.start_date,
      endDate: newzooDateRange?.end_date,
    });
  });

  /**
   * 指标宽表组件刷新依赖项
   */
  const tableUpdateDeps = useMemo<React.DependencyList>(() => [], [locale, filterValue, newzooDateRange]);

  /**
   * 刷新页面列表数据（按market拆分）
   * 表格、echarts世界地图
   */
  const updateData = async (
    filterValuePayload = filterValue,
    metricsPayload = granularityMetrics,
    sortsPayload = sorts,
    checkMetrics?: string[],
  ) => {
    setMarketProfilesDataLoading(true);
    try {
      const filterFetchParams = formatFilterValue(filterValuePayload);
      const metricKeys = metricsPayload?.map((item) => item?.key) || [];
      const res = await fetchGenreMarketProfileList({
        ...filterFetchParams,
        metrics: [...metricKeys, ...YEARLY_METRIC_KEYS],
        check_metrics: checkMetrics,
        order_by: generateSortMetricKeys(sortsPayload),
        entity_type: entityType,
      });
      if (res.paramsData) setReqParams(res.paramsData);
      const resData = res?.data || {};
      const marketProfileList = addRank(multiRatioMetricSort(resData?.list || [], sorts));
      const marketProfilesDataNew = { ...marketProfilesData, ...resData, list: marketProfileList };
      const pagingQueryNew = { ...pagingQuery, pageIndex: 1 };
      setCountries(getTopCountries(marketProfileList));
      setMarketProfilesData(marketProfilesDataNew);
      setPagingQuery(pagingQueryNew);
      marketProfilesDataCached.current = marketProfilesDataNew;
      pagingQueryCached.current = pagingQueryNew;

      const req = res?.req || {};
      const newzooStartDate = req?.newzoo_start_date || '';
      const newzooEndDate = req?.newzoo_end_date || '';
      setNewzooDateRange({ start_date: newzooStartDate, end_date: newzooEndDate });

      if (!filterFetchParams?.start_date || !filterFetchParams?.end_date) {
        const granularity = req?.granularity || 'monthly';
        const startDate = req?.start_date || '';
        const endDate = req?.end_date || '';
        const rangeTimeFilterValue = getRangeTimeFilterValueByDateRangeFetchParams({
          granularity,
          start_date: startDate,
          end_date: endDate,
        });
        updateFilterValue({
          ...filterValuePayload,
          [filterKeyMap.RANGE_TIME]: {
            ...filterValuePayload?.[filterKeyMap.RANGE_TIME],
            ...rangeTimeFilterValue,
          },
        });
      }
    } finally {
      setMarketProfilesDataLoading(false);
    }
  };

  /**
   * 刷新页面列表数据（按date、market拆分）
   * 仅用于echarts折线图
   */
  const updateLineChartData = async (
    filterValuePayload = filterValue,
    metricsPayload = granularityMetrics,
    checkMetrics?: string[],
  ) => {
    try {
      setMarketProfilesDataGroupedByDateLoading(true);
      const metricKeys = metricsPayload?.map((item) => item?.key) || [];
      const filterFetchParams = formatFilterValue(filterValuePayload);
      const res = await fetchGenreMarketProfileLineChartList({
        ...filterFetchParams,
        metrics: [...metricKeys, ...YEARLY_METRIC_KEYS],
        check_metrics: checkMetrics,
      });
      setMarketProfilesDataGroupedByDate({ ...marketProfilesDataGroupedByDate, ...res?.data });
    } finally {
      setMarketProfilesDataGroupedByDateLoading(false);
    }
  };

  /**
   * 筛选器change的回调
   * @param filterValuePayload 筛选器value
   * @param filterApiValuePayload 筛选器的apiValue
   */
  const handleFilterChange = (filterValuePayload: IRecord, filterApiValuePayload: IRecord) => {
    const rangeTimePayload = filterApiValuePayload?.[filterKeyMap.RANGE_TIME];
    const dateRangeFetchParamsPayload = getDateRangeFetchParamsByRangeTimeFilterValue(rangeTimePayload);
    const granularityPayload = dateRangeFetchParamsPayload.granularity;

    const rangeTime = filterValue?.[filterKeyMap.RANGE_TIME];
    const dateRangeFetchParams = getDateRangeFetchParamsByRangeTimeFilterValue(rangeTime);

    if (granularityPayload === dateRangeFetchParams.granularity) {
      updateData(filterValuePayload);
      updateLineChartData(filterValuePayload);
      updateFilterValue(filterValuePayload);
    } else {
      const filterValuePayloadNew = {
        ...filterValuePayload,
        [filterKeyMap.GENRETOR]: {
          ...defaultFilterValue[filterKeyMap.GENRETOR],
        },
        [filterKeyMap.REGION]: {
          ...defaultFilterValue[filterKeyMap.REGION],
        },
      };

      const metricsNew = getGranularityMetrics(granularityPayload);
      const metricNew = getGranularityMetric(granularityPayload);
      const sortsNew = getGranularitySorts(granularityPayload);

      updateData(filterValuePayloadNew, metricsNew, sortsNew);
      updateLineChartData(filterValuePayloadNew, metricsNew);
      updateFilterValue(filterValuePayloadNew);
      setMetric({ ...metricNew, granularity: granularityPayload });
      setSorts(sortsNew);
    }
  };

  /**
   * 获取数据表下载的接口入参
   */
  const getDownloadReqParams = useMemoizedFn(() => {
    return {
      ...reqParams,
      regionOptions,
      markets: marketProfilesData?.list?.map((item) => item?.market) || [],
      entity_type: entityType,
    };
  });

  /**
   * 切换echarts图表类型
   * @param chartType echarts图表类型
   */
  const handleChartTypeChange = (chartType: ChartType) => {
    setChartType(chartType);
  };

  const handleMetricRedirectConfirm = (granularity: IGranularity, metric: IPrimaryKeyMetric) => {
    const rangeTimeFilterValueNew = getRangeTimeFilterValueByDateRangeFetchParams({
      granularity,
      start_date: '',
      end_date: '',
    });
    const filterValueNew = {
      ...filterValue,
      [filterKeyMap.RANGE_TIME]: {
        ...filterValue?.[filterKeyMap.RANGE_TIME],
        ...rangeTimeFilterValueNew,
      },
    };
    const metricsNew = getGranularityMetrics(granularity);
    const sortsNew = getGranularitySorts(granularity);
    const checkMetricsNew = !!metric ? [metric.key] : undefined;

    updateData(filterValueNew, metricsNew, sortsNew, checkMetricsNew);
    updateLineChartData(filterValueNew, metricsNew, checkMetricsNew);
    updateFilterValue(filterValueNew);
    setSorts(sortsNew);
    setMetric(metric);
    setFilterRedirectConfirmGranularity('');
    setFilterRedirectConfirmMetric(null);
    setFilterRedirectConfirmModalVisible(false);

    reportButtonLog(dbiBtnLogMes.marketProfileCombineMetricSelectJump);
  };

  /**
   * 切换echarts图表的选中指标
   * @param metric 选中指标
   * @param granularity 选中指标归属的日期粒度
   */
  const handleMetricChange = (metric: IPrimaryKeyMetric, granularity: IGranularity) => {
    const metricNew = { ...metric, granularity };
    if (getIsNewzooYearlyMetric(metric)) {
      setMetric(metricNew);
      return;
    }

    const rangeTime = filterValue?.[filterKeyMap.RANGE_TIME];
    const dateRangeFetchParams = getDateRangeFetchParamsByRangeTimeFilterValue(rangeTime);
    // 切换指标后，是否会导致日期粒度的改变
    const isGranularityChanged = granularity !== dateRangeFetchParams.granularity;

    if (!isGranularityChanged) {
      if (
        GSD_DIGITAL_REVENUE_METRIC_KEY !== metric?.key ||
        !getIsMetricNoData(metric, marketProfilesDataCached.current?.list)
      ) {
        setMetric(metricNew);
        return;
      }

      setFilterRedirectConfirmModalVisible(true);
      setFilterRedirectConfirmGranularity(granularity);
      setFilterRedirectConfirmMetric(metricNew);
    } else {
      if (getIsShowByShowDate(showDate)) {
        setFilterRedirectConfirmModalVisible(true);
        setFilterRedirectConfirmGranularity(granularity);
        setFilterRedirectConfirmMetric(metricNew);
      } else {
        const rangeTimeFilterValue = getRangeTimeFilterValueByDateRangeFetchParams({
          granularity,
          start_date: '',
          end_date: '',
        });
        const filterValueNew = {
          ...filterValue,
          [filterKeyMap.RANGE_TIME]: {
            ...filterValue?.[filterKeyMap.RANGE_TIME],
            ...rangeTimeFilterValue,
          },
        };
        const metricsNew = getGranularityMetrics(granularity);
        const sortsNew = getGranularitySorts(granularity);
        const checkMetricsNew = !!metricNew ? [metricNew.key] : undefined;

        updateData(filterValueNew, metricsNew, sortsNew, checkMetricsNew);
        updateLineChartData(filterValueNew, metricsNew, checkMetricsNew);
        updateFilterValue(filterValueNew);
        setSorts(sortsNew);
        setMetric(metricNew);
      }
    }
  };

  /**
   * 处理排序
   * @param sorts 排序状态列表
   */
  const handleSortsChange = useMemoizedFn((sorts: IRatioMetricSortState<IGenreMarketProfile>[]) => {
    const marketProfileList = addRank(multiRatioMetricSort(marketProfilesData?.list || [], sorts));
    const marketProfilesDataNew: IGenreMarketProfilePaginationData = { ...marketProfilesData, list: marketProfileList };
    const pagingQueryNew = { ...pagingQuery, pageIndex: 1 };
    setMarketProfilesData(marketProfilesDataNew);
    setSorts(sorts);
    setPagingQuery(pagingQueryNew);
    marketProfilesDataCached.current = marketProfilesDataNew;
    pagingQueryCached.current = pagingQueryNew;
  });

  const handlePagingChange = (pagingQuery: PagingQuery) => {
    setPagingQuery(pagingQuery);
    pagingQueryCached.current = pagingQuery;
  };

  /**
   * 页面初始化
   */
  useEffect(() => {
    if (isLoading) return;
    if (genresOptionsResLoading) return;
    const genreDecodeObj = decodeObj?.[filterKeyMap.GENRETOR];
    const genreSourceDecodeObj = genreDecodeObj?.source || 'iegg';
    const genreMainGenresDecodeObj = genreDecodeObj?.mainGenres || [];
    const genreSubGenresDecodeObj = genreDecodeObj?.subGenres || [];
    const rangeTimeDecodeObj = decodeObj?.[filterKeyMap.RANGE_TIME];
    const regionDecodeObj = decodeObj?.[filterKeyMap.REGION];
    const regionMarketsDecodeObj = regionDecodeObj?.markets || ['global'];
    const filterValueNew = {
      ...filterValue,
      [filterKeyMap.GENRETOR]: {
        ...filterValue?.[filterKeyMap.GENRETOR],
        source: genreSourceDecodeObj,
        mainGenres: calcMainGenres(
          genreSourceDecodeObj,
          genreMainGenresDecodeObj,
          genreSubGenresDecodeObj,
          genresOptionsRes?.data,
        ),
        subGenres: genreSubGenresDecodeObj,
        options: genresOptionsRes?.data?.[GENRE_SOURCE_REVERSE_MAP[genreSourceDecodeObj]],
      },
      [filterKeyMap.RANGE_TIME]: {
        ...filterValue?.[filterKeyMap.RANGE_TIME],
        type: rangeTimeDecodeObj?.type || 'month',
        value: rangeTimeDecodeObj?.value || [undefined, undefined],
      },
      [filterKeyMap.REGION]: {
        ...filterValue?.[filterKeyMap.REGION],
        markets: regionMarketsDecodeObj || ['global'],
        realMarkets: regionMarketsDecodeObj?.includes('global') ? [] : regionMarketsDecodeObj,
      },
    };
    const rangeTimeNew = filterValueNew?.[filterKeyMap.RANGE_TIME];
    const dateRangeFetchParamsNew = getDateRangeFetchParamsByRangeTimeFilterValue(rangeTimeNew);
    const granularityNew = dateRangeFetchParamsNew.granularity;
    const granularityMetricsNew = getGranularityMetrics(granularityNew);
    const metricNew = getGranularityMetric(granularityNew);
    const sortsNew = getGranularitySorts(granularityNew);
    const checkMetricsNew = !!metricNew ? [metricNew.key] : undefined;
    setMetric(metricNew);
    setSorts(sortsNew);
    updateData(filterValueNew, granularityMetricsNew, sortsNew, checkMetricsNew);
    updateLineChartData(filterValueNew, granularityMetricsNew, checkMetricsNew);
    updateFilterValue(filterValueNew);
  }, [isLoading, genresOptionsResLoading]);

  return (
    <div style={style} className={classnames('dbi-market-profile dbi-market-profile-pc-console', className)}>
      <Filter
        genresOptionsDataLoading={genresOptionsResLoading}
        genresOptionsData={genresOptionsRes?.data}
        regionOptions={regionOptions}
        getRadioOptionsTips={getRadioOptionsTips}
        defaultValue={defaultFilterValue}
        value={filterValue}
        onChange={handleFilterChange}
      />
      <GlobalPerformance
        domain="dbi-market-profile-pc-console"
        loading={chartType === ChartType.Line ? marketProfilesDataGroupedByDateLoading : marketProfilesDataLoading}
        noData={!marketProfilesData?.list?.length}
        summarySourceList={DATASOURCE_EXPLANATION_KEY_LIST}
        summarySourceKeyList={DATASOURCE_EXPLANATION_DATASOURCE_KEY_LIST}
        htmlDataSourceList={DATASOURCE_EXPLANATION_HTML_DATASOURCE_LIST}
        htmlContentMap={getDataSourceExplanationHtmlContent(locale)}
        countryMap={countryMap}
        regionOptions={regionOptions}
        countries={countries}
        chartType={chartType}
        onChartTypeChange={handleChartTypeChange}
        metric={metric}
        metricName={getMetricNameSpecialInPage(metric, getMetricName(locale, metric?.dataSource, metric?.metric))}
        mapData={mapData}
        lineData={lineData}
        isExplanation={true}
        metricSelect={
          <GroupByGranularityMetricSelect
            className="dbi-market-profile__metric-select"
            getMetricNameSpecial={getMetricNameSpecialInPage}
            getDataSourceHighlightIcon={(dataSource) => {
              return dataSource !== 'newzoo' ? '' : getDataSourceIcon('newzoowhite');
            }}
            options={[{
              granularity: 'monthly',
              metrics: metrics
            }]}
            value={metric}
            onChange={handleMetricChange}
            isNoGranularityName={true}
          />
        }
      />
      {/* 下载功能 */}
      <Justify
        className="dbi-card-justify"
        right={
          <div className="justify-right">
            <SearchTable
              regionsCountries={regionOptions}
              countrieSelectList={countriesForSearch}
              allCountriesList={countriesForSearch}
              handleSearchData={(value) => {
                if (value === '') {
                  setMarketProfilesData(marketProfilesDataCached.current);
                  setPagingQuery(pagingQueryCached.current);
                  setIsSearchMode(false);
                } else {
                  const marketProfileDataListNew = marketProfilesDataCached.current?.list?.filter((item) => {
                    const country = countryMap[item?.market?.toLowerCase()];
                    const nameEN = country?.country_name_en?.toLowerCase();
                    const nameCN = country?.country_name_cn;
                    return nameEN?.includes(value?.toLowerCase()) || nameCN.includes(value);
                  });
                  if (!!marketProfileDataListNew?.length) {
                    const marketProfilesDataNew = {
                      ...marketProfilesData,
                      total: marketProfileDataListNew?.length ?? 0,
                      list: marketProfileDataListNew ?? [],
                    };
                    setMarketProfilesData(marketProfilesDataNew);
                    setPagingQuery({ ...pagingQuery, pageIndex: 1 });
                    setIsSearchMode(true);
                  }
                  reportSearchLog(dbiPageLogMes.marketProfileCombine, { query: value, clickId: value });
                }
              }}
            />
            <ExportTable
              useWebDownload
              isNeedCheckedLines
              downloadModalSize={900}
              entityType={entityType}
              // 'genre-profile' 控制弹窗显示内容
              businessType={`marketProfile-combine`}
              newStyleFileName="Market Profile_PC&Console Games"
              // @ts-ignore
              total={marketProfilesData?.total}
              metricSelectorOptions={metrics.map((item) => {
                const metricName = getMetricNameSpecialInPage(item, item?.metricName?.[locale]);
                // @ts-ignore
                return { ...item, value: item?.key, metricName };
              })} // 数据源
              isDisableDownload={
                chartType === ChartType.Line ? marketProfilesDataGroupedByDateLoading : marketProfilesDataLoading
              }
              downloadBtnLogConfig={dbiBtnLogMes?.marketProfileCombineDownloadExcel}
              downloadKey={`combineMarketProfileTopCharts`}
              tableParams={getDownloadReqParams()}
              getCheckedLinesCfg={() => {
                // 非搜索模式下，直接返回当前勾选的markets即可
                if (!isSearchMode) return { key: 'markets', value: countries };
                // 搜索状态下，需要把当前勾选的markets调整为仅包含当前搜索到的markets
                const searchModeMarkets = marketProfilesData?.list?.map((item) => item?.market) || [];
                return {
                  key: 'markets',
                  value: countries?.filter((item) => {
                    return searchModeMarkets?.includes(item);
                  }),
                };
              }}
            />
          </div>
        }
      />
      <MarketMetricsTable
        loading={marketProfilesDataLoading}
        noData={!marketProfilesData?.list?.length}
        entityType={entityType}
        metrics={granularityMetrics}
        data={marketProfilesData}
        countryMap={countryMap}
        checkedList={countries}
        onCheckedListChange={setCountries}
        sorts={sorts}
        onSortsChange={handleSortsChange}
        pagingQuery={pagingQuery}
        onPagingChange={handlePagingChange}
        getMetricNameSpecial={getMetricNameSpecialInPage}
        updateDeps={tableUpdateDeps}
      />
      {/* 筛选器跳转二次确认弹窗 */}
      <MetricSelectConfirmModal
        visible={filterRedirectConfirmModalVisible}
        metric={filterRedirectConfirmMetric}
        onConfirm={(dontShowAgain) => {
          if (dontShowAgain) {
            updateDontShowAgain('false');
          }
          handleMetricRedirectConfirm(filterRedirectConfirmGranularity, filterRedirectConfirmMetric!);
        }}
        onCancel={() => {
          setFilterRedirectConfirmModalVisible(false);
          setFilterRedirectConfirmGranularity('');
          setFilterRedirectConfirmMetric(null);
        }}
        onClose={() => {
          setFilterRedirectConfirmModalVisible(false);
          setFilterRedirectConfirmGranularity('');
          setFilterRedirectConfirmMetric(null);
        }}
      />
    </div>
  );
};

export default PCAndConsoleGames;
